import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AddNote from '../../components/notes/add-note/add-note.component';
import AdminModal from '../../components/reusable/admin-modal/admin-modal.component';
import Button from '../../components/reusable/button/button.component';
import ComponentModal from '../../components/reusable/component-modal/component-modal.component';
import FulfillmentWrapper from '../../components/fulfillment/fulfillment-wrapper/fulfillment-wrapper.component';
import NoteCard from '../../components/notes/note-card/note-card.component';
import NoteDisplay from '../../components/notes/note-display/note-display.component';
import OrderDisplay from '../../components/order/order-display/order-display.component';
import ProcessOrder from '../../components/order/process-order/process-order.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import UpdateNote from '../../components/notes/update-note/update-note.component';
import UpdateOrder from '../../components/order/update-order/update-order.component';

import Client from '../../tools/client';
import { ensureSocketConnected, onSafe } from '../../tools/socket';

import {
  MainContainer,
  SectionContainer,
} from './order.styles';

import {
  BackLink,
  ContentContainer,
  MainTitle,
  Subtitle
} from '../../styles/page.styles';

const client = new Client();

const OrderPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [showUpdate, setShowUpdate] = useState(false);
  const [order, setOrder] = useState(null);
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [action, setAction] = useState('');
  const [actionText, setActionText] = useState('');

  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showNoteDisplayModal, setShowNoteDisplayModal] = useState(false);
  const [showNoteEditModal, setShowNoteEditModal] = useState(false);

  const [showFulfillmentDisplay, setShowFulfillmentDisplay] = useState(false);
  const [currentFulfillment, setCurrentFulfillment] = useState({});

  useEffect(() => {
    ensureSocketConnected();

    const offNewOrder = onSafe('newOrder', (data) => {
      if (data?.id?.toString() === id?.toString()) {
        getOrder();
      }
    });

    return () => {
      offNewOrder();
    };
  }, []);

  useEffect(() => {
    getOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOrder = async () => {
    setLoading(true);
    const orderRes = await client.getOrderById(id);
    setOrder(orderRes);
    setUser(orderRes.User);
    setProducts(orderRes.products); // product + inventory already hydrated
    setLoading(false);
  };

  const onRefresh = async () => {
    const orderRes = await client.getOrderById(id);
    setOrder(orderRes);
    setUser(orderRes.User);
    setProducts(orderRes.products);

    const refreshedNote = orderRes.OrderNotes?.find(
      (entry) => entry.Note.id === selectedNote?.id
    )?.Note;

    if (refreshedNote) setSelectedNote({ ...refreshedNote });
  };

  const updateOrder = async (params) => {
    await client.updateOrder(params);
    await getOrder();
    setShowUpdate(false);
  };

  const cancelOrder = async () => {
    await client.cancelOrder(order.id);
    await getOrder();
    setShowUpdate(false);
    setShowOrderModal(false);
  };

  const confirmCancelOrder = () => {
    setTitle('Cancel Order');
    setMessage(`Are you sure you want to cancel order ${order.id}? This will restock all inventory from the order and cannot be undone.`);
    setAction(() => cancelOrder);
    setActionText('Cancel Order');
    setShowOrderModal(true);
  };

  const display = () => {
    if (showUpdate) {
      return (
        <UpdateOrder
          order={order}
          updateOrder={updateOrder}
          cancelOrder={confirmCancelOrder}
          addInventoryToOrder={() => {}}
          removeInventoryFromOrder={() => {}}
          showUpdate={setShowUpdate}
        />
      );
    }

    return (
      <SectionContainer>
        <OrderDisplay user={user} order={order} products={products} showUpdate={setShowUpdate} />
        <ContentContainer margin={'20px 0'}>
          <Button onClick={() => setShowAddNoteModal(true)}>Add Note</Button>
        </ContentContainer>

        {order.OrderNotes?.length > 0 && (
          order.OrderNotes.map((entry, idx) => (
            <NoteCard
              key={idx}
              note={entry.Note}
              createdAt={entry.createdAt}
              onClick={() => {
                setSelectedNote(entry.Note);
                setShowNoteDisplayModal(true);
              }}
            />
          ))
        )}

        <ProcessOrder user={user} order={order} getOrder={getOrder} />

        <FulfillmentWrapper orderId={order.id} getOrder={getOrder} />
      </SectionContainer>
    );
  };

  return (
    <MainContainer>
      <BackLink onClick={() => navigate(`/orders`)}>
        Back To Orders
      </BackLink>

      <AdminModal
        show={showOrderModal}
        setShow={setShowOrderModal}
        title={title}
        image={''}
        message={message}
        cancelText={'Back'}
        action={action}
        actionText={actionText}
      />

      <ComponentModal isOpen={showAddNoteModal} onClose={() => setShowAddNoteModal(false)} title="Add Note">
        <AddNote
          onSubmit={async (formData) => {
            formData.append('orderId', order.id);
            await client.createOrderNote(formData);
            await getOrder();
            setShowAddNoteModal(false);
          }}
        />
      </ComponentModal>

      <ComponentModal isOpen={showNoteDisplayModal} onClose={() => setShowNoteDisplayModal(false)} title="Order Note">
        <NoteDisplay
          note={selectedNote}
          setShowEdit={() => {
            setShowNoteDisplayModal(false);
            setShowNoteEditModal(true);
          }}
          onRefresh={onRefresh}
        />
      </ComponentModal>

      <ComponentModal isOpen={showNoteEditModal} onClose={() => setShowNoteEditModal(false)} title="Edit Note">
        <UpdateNote
          note={selectedNote}
          onUpdate={async (noteId, updatedData) => {
            await client.updateNote(noteId, updatedData);
            await getOrder();
            setShowNoteEditModal(false);
          }}
          onDelete={async (noteId) => {
            await client.deleteOrderNote(order.id, noteId);
            await getOrder();
            setShowNoteEditModal(false);
          }}
          onClose={() => setShowNoteEditModal(false)}
        />
      </ComponentModal>

      {loading ? <Spinner /> : display()}
    </MainContainer>
  );
};

export default OrderPage;